import { useState, useRef, useEffect } from 'react'
import { useUIStore } from '@/stores'
import { useAppProjects, useAppProject, useAvailableModels, useCreateAppProject, useDeleteAppProject, useSaveAppFile, useDeleteAppFile, useGenerateAppCode, useResolveAppError, useClearAppErrors } from '@/hooks'
import { api } from '@/services/api-client'
import { useT } from '@/i18n'
import { Plus, Trash2, FileCode, Eye, AlertTriangle, Download, Send, ChevronRight, ChevronDown, Folder, File, X, Check, RefreshCw, ArrowLeft } from 'lucide-react'

type Tab = 'code' | 'preview' | 'errors'

export function AppBuilderView() {
  const { activeAppProjectId, navigateToAppBuilder } = useUIStore()

  if (activeAppProjectId) {
    return <AppEditor projectId={activeAppProjectId} onBack={() => navigateToAppBuilder()} />
  }

  return <AppProjectList />
}

function AppProjectList() {
  const t = useT()
  const { navigateToAppBuilder } = useUIStore()
  const { data: projects } = useAppProjects()
  const createProject = useCreateAppProject()
  const deleteProject = useDeleteAppProject()
  const [showNew, setShowNew] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')

  const handleCreate = () => {
    if (!newName.trim()) return
    createProject.mutate({ name: newName.trim(), description: newDesc.trim() }, {
      onSuccess: (data: any) => {
        setNewName('')
        setNewDesc('')
        setShowNew(false)
        navigateToAppBuilder(data.id)
      },
    })
  }

  return (
    <div className="app-builder">
      <div className="app-builder-header">
        <div>
          <h1>{t.appBuilderTitle}</h1>
          <p className="app-builder-subtitle">{t.appBuilderDesc}</p>
        </div>
        <button className="button-primary" onClick={() => setShowNew(true)}>
          <Plus size={14} /> {t.appBuilderNew}
        </button>
      </div>

      {showNew && (
        <div className="app-builder-new-form">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNew(false) }}
            placeholder={t.appBuilderName}
            className="app-builder-input"
          />
          <input
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNew(false) }}
            placeholder={t.appBuilderDesc}
            className="app-builder-input"
          />
          <div className="app-builder-new-actions">
            <button className="button-primary" onClick={handleCreate}>{t.create}</button>
            <button onClick={() => setShowNew(false)}>{t.cancel}</button>
          </div>
        </div>
      )}

      {projects && projects.length > 0 ? (
        <div className="app-builder-grid">
          {projects.map((p: any) => (
            <div key={p.id} className="app-builder-card" onClick={() => navigateToAppBuilder(p.id)}>
              <div className="app-builder-card-header">
                <FileCode size={20} />
                <h3>{p.name}</h3>
              </div>
              {p.description && <p className="app-builder-card-desc">{p.description}</p>}
              <div className="app-builder-card-meta">
                <span>{new Date(p.created_at).toLocaleDateString()}</span>
                {p.model && <span>{p.model}</span>}
              </div>
              <button
                className="app-builder-card-delete"
                onClick={(e) => {
                  e.stopPropagation()
                  if (confirm(t.appBuilderDeleteConfirm)) deleteProject.mutate(p.id)
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>{t.appBuilderNoProjects}</p>
        </div>
      )}
    </div>
  )
}

function AppEditor({ projectId, onBack }: { projectId: string; onBack: () => void }) {
  const t = useT()
  const { data: project, refetch } = useAppProject(projectId)
  const { data: availableModels } = useAvailableModels()
  const generateCode = useGenerateAppCode()
  const saveFile = useSaveAppFile()
  const deleteFile = useDeleteAppFile()
  const resolveError = useResolveAppError()
  const clearErrors = useClearAppErrors()

  const [activeTab, setActiveTab] = useState<Tab>('code')
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [prompt, setPrompt] = useState('')
  const [provider, setProvider] = useState('')
  const [model, setModel] = useState('')
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set())
  const [showNewFile, setShowNewFile] = useState(false)
  const [newFilePath, setNewFilePath] = useState('')
  const [summary, setSummary] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const editorRef = useRef<HTMLTextAreaElement>(null)

  const files = project?.files || []
  const errors = project?.errors || []
  const unresolvedErrors = errors.filter((e: any) => !e.resolved)

  const selectedFile = files.find((f: any) => f.id === selectedFileId)
  const selectedProviderModels = availableModels?.find((p: any) => p.provider_type === provider)?.models || []

  useEffect(() => {
    if (availableModels && availableModels.length > 0 && !provider) {
      const first = availableModels[0]
      setProvider(first.provider_type)
      setModel(first.models[0] || '')
    }
  }, [availableModels])

  useEffect(() => {
    if (selectedFile && !selectedFile.is_directory) {
      setEditContent(selectedFile.content || '')
    }
  }, [selectedFileId, selectedFile?.content])

  const handleGenerate = () => {
    if (!prompt.trim() || generateCode.isPending) return
    generateCode.mutate(
      { projectId, prompt: prompt.trim(), provider: provider || undefined, model: model || undefined },
      {
        onSuccess: (data) => {
          setSummary(data.summary || '')
          setPrompt('')
          refetch()
        },
      }
    )
  }

  const handleSaveFile = () => {
    if (!selectedFile) return
    saveFile.mutate({ projectId, path: selectedFile.path, content: editContent })
  }

  const handleCreateFile = () => {
    if (!newFilePath.trim()) return
    saveFile.mutate(
      { projectId, path: newFilePath.trim(), content: '' },
      { onSuccess: () => { setNewFilePath(''); setShowNewFile(false); refetch() } }
    )
  }

  const handleDeleteFile = (fileId: string) => {
    deleteFile.mutate({ projectId, fileId }, { onSuccess: () => { if (selectedFileId === fileId) setSelectedFileId(null); refetch() } })
  }

  const handleExport = () => {
    const url = api.getAppExportUrl(projectId)
    window.open(url, '_blank')
  }

  const toggleDir = (path: string) => {
    const next = new Set(expandedDirs)
    if (next.has(path)) next.delete(path); else next.add(path)
    setExpandedDirs(next)
  }

  // Build file tree structure
  const fileTree = buildFileTree(files)

  return (
    <div className="app-editor">
      {/* Top bar */}
      <div className="app-editor-topbar">
        <button className="app-editor-back" onClick={onBack}>
          <ArrowLeft size={16} /> {t.back}
        </button>
        <h2>{project?.name || '...'}</h2>
        <div className="app-editor-tabs">
          <button className={`app-editor-tab ${activeTab === 'code' ? 'active' : ''}`} onClick={() => setActiveTab('code')}>
            <FileCode size={14} /> {t.appBuilderCode}
          </button>
          <button className={`app-editor-tab ${activeTab === 'preview' ? 'active' : ''}`} onClick={() => setActiveTab('preview')}>
            <Eye size={14} /> {t.appBuilderPreview}
          </button>
          <button className={`app-editor-tab ${activeTab === 'errors' ? 'active' : ''}`} onClick={() => setActiveTab('errors')}>
            <AlertTriangle size={14} /> {t.appBuilderErrors}
            {unresolvedErrors.length > 0 && <span className="app-editor-badge">{unresolvedErrors.length}</span>}
          </button>
        </div>
        <button className="button-primary" onClick={handleExport} title={t.appBuilderExport}>
          <Download size={14} /> {t.appBuilderExport}
        </button>
      </div>

      <div className="app-editor-body">
        {/* File sidebar */}
        <div className="app-editor-sidebar">
          <div className="app-editor-sidebar-header">
            <span>{t.appBuilderFiles}</span>
            <button className="app-editor-sidebar-btn" onClick={() => setShowNewFile(true)} title={t.appBuilderNewFile}>
              <Plus size={12} />
            </button>
          </div>

          {showNewFile && (
            <div className="app-editor-new-file">
              <input
                autoFocus
                value={newFilePath}
                onChange={(e) => setNewFilePath(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFile(); if (e.key === 'Escape') setShowNewFile(false) }}
                placeholder={t.appBuilderFilePath}
                className="app-editor-inline-input"
              />
            </div>
          )}

          <div className="app-editor-file-tree">
            {fileTree.length > 0 ? (
              fileTree.map((node) => (
                <FileTreeNode
                  key={node.path}
                  node={node}
                  selectedFileId={selectedFileId}
                  expandedDirs={expandedDirs}
                  onSelect={(id) => { setSelectedFileId(id); setActiveTab('code') }}
                  onToggleDir={toggleDir}
                  onDelete={handleDeleteFile}
                />
              ))
            ) : (
              <p className="app-editor-empty-files">{t.appBuilderNoFiles}</p>
            )}
          </div>
        </div>

        {/* Main content area */}
        <div className="app-editor-main">
          {activeTab === 'code' && (
            <div className="app-editor-code-panel">
              {selectedFile && !selectedFile.is_directory ? (
                <>
                  <div className="app-editor-code-header">
                    <span className="app-editor-file-path">{selectedFile.path}</span>
                    <span className="app-editor-file-lang">{selectedFile.language}</span>
                    <button className="button-primary button-sm" onClick={handleSaveFile} disabled={saveFile.isPending}>
                      {t.appBuilderSaveFile}
                    </button>
                  </div>
                  <textarea
                    ref={editorRef}
                    className="app-editor-textarea"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    spellCheck={false}
                    wrap="off"
                  />
                </>
              ) : (
                <div className="app-editor-no-file">
                  <FileCode size={48} />
                  <p>{t.appBuilderNoFiles}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="app-editor-preview-panel">
              <div className="app-editor-preview-header">
                <span>{t.appBuilderPreview}</span>
                <button className="app-editor-sidebar-btn" onClick={() => refetch()} title="Refresh">
                  <RefreshCw size={12} />
                </button>
              </div>
              <iframe
                className="app-editor-preview-iframe"
                src={api.getAppPreviewUrl(projectId)}
                sandbox="allow-scripts allow-modals"
                title="App Preview"
              />
            </div>
          )}

          {activeTab === 'errors' && (
            <div className="app-editor-errors-panel">
              <div className="app-editor-errors-header">
                <span>{t.appBuilderErrors} ({unresolvedErrors.length})</span>
                {errors.length > 0 && (
                  <button className="button-sm" onClick={() => clearErrors.mutate(projectId)}>
                    {t.appBuilderClearErrors}
                  </button>
                )}
              </div>
              {errors.length > 0 ? (
                <div className="app-editor-errors-list">
                  {errors.map((err: any) => (
                    <div key={err.id} className={`app-editor-error-item ${err.resolved ? 'resolved' : ''} ${err.type}`}>
                      <div className="app-editor-error-icon">
                        <AlertTriangle size={14} />
                      </div>
                      <div className="app-editor-error-content">
                        <span className="app-editor-error-message">{err.message}</span>
                        {err.file_path && <span className="app-editor-error-file">{err.file_path}{err.line_number > 0 ? `:${err.line_number}` : ''}</span>}
                        <span className="app-editor-error-time">{new Date(err.created_at).toLocaleString()}</span>
                      </div>
                      {!err.resolved && (
                        <button className="app-editor-error-resolve" onClick={() => resolveError.mutate({ projectId, errorId: err.id })}>
                          <Check size={12} /> {t.appBuilderResolve}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="app-editor-no-errors">
                  <Check size={32} />
                  <p>{t.appBuilderNoErrors}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Prompt bar at bottom */}
      <div className="app-editor-prompt-bar">
        {summary && (
          <div className="app-editor-summary">
            <strong>{t.appBuilderSummary}:</strong> {summary}
            <button className="app-editor-summary-close" onClick={() => setSummary('')}><X size={12} /></button>
          </div>
        )}
        <div className="app-editor-prompt-row">
          <div className="app-editor-model-selectors">
            <select
              className="app-editor-select"
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value)
                const models = availableModels?.find((p: any) => p.provider_type === e.target.value)?.models || []
                setModel(models[0] || '')
              }}
            >
              <option value="">{t.threadSelectProvider}</option>
              {availableModels?.map((p: any) => (
                <option key={p.provider_type} value={p.provider_type}>{p.display_name}</option>
              ))}
            </select>
            {provider && selectedProviderModels.length > 0 && (
              <select
                className="app-editor-select"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                {selectedProviderModels.map((m: string) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            )}
          </div>
          <textarea
            ref={textareaRef}
            className="app-editor-prompt-input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate() }
            }}
            placeholder={t.appBuilderPrompt}
            rows={2}
          />
          <button
            className="button-primary app-editor-send-btn"
            onClick={handleGenerate}
            disabled={generateCode.isPending || !prompt.trim()}
          >
            {generateCode.isPending ? <RefreshCw size={16} className="spin" /> : <Send size={16} />}
          </button>
        </div>
        {generateCode.isPending && (
          <div className="app-editor-generating">{t.appBuilderGenerating}</div>
        )}
      </div>
    </div>
  )
}

// File tree types and builder
interface TreeNode {
  name: string
  path: string
  id: string
  isDirectory: boolean
  language: string
  children: TreeNode[]
}

function buildFileTree(files: any[]): TreeNode[] {
  const root: TreeNode[] = []
  const dirMap: Record<string, TreeNode> = {}

  // Sort: directories first, then by path
  const sorted = [...files].sort((a, b) => {
    if (a.is_directory && !b.is_directory) return -1
    if (!a.is_directory && b.is_directory) return 1
    return a.path.localeCompare(b.path)
  })

  for (const file of sorted) {
    const parts = file.path.split('/')
    const name = parts[parts.length - 1]
    const node: TreeNode = {
      name,
      path: file.path,
      id: file.id,
      isDirectory: !!file.is_directory,
      language: file.language || '',
      children: [],
    }

    if (file.is_directory) {
      dirMap[file.path] = node
    }

    if (parts.length === 1) {
      root.push(node)
    } else {
      const parentPath = parts.slice(0, -1).join('/')
      const parent = dirMap[parentPath]
      if (parent) {
        parent.children.push(node)
      } else {
        root.push(node)
      }
    }
  }

  return root
}

function FileTreeNode({ node, selectedFileId, expandedDirs, onSelect, onToggleDir, onDelete, depth = 0 }: {
  node: TreeNode; selectedFileId: string | null; expandedDirs: Set<string>
  onSelect: (id: string) => void; onToggleDir: (path: string) => void; onDelete: (id: string) => void
  depth?: number
}) {
  const isExpanded = expandedDirs.has(node.path)
  const isSelected = selectedFileId === node.id

  return (
    <>
      <div
        className={`app-file-tree-item ${isSelected ? 'selected' : ''} ${node.isDirectory ? 'directory' : 'file'}`}
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={() => node.isDirectory ? onToggleDir(node.path) : onSelect(node.id)}
      >
        {node.isDirectory ? (
          <>
            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <Folder size={14} className="tree-icon folder-icon" />
          </>
        ) : (
          <>
            <span style={{ width: 12 }} />
            <File size={14} className="tree-icon file-icon" />
          </>
        )}
        <span className="app-file-tree-name">{node.name}</span>
        {!node.isDirectory && (
          <button className="app-file-tree-delete" onClick={(e) => { e.stopPropagation(); onDelete(node.id) }}>
            <X size={10} />
          </button>
        )}
      </div>
      {node.isDirectory && isExpanded && node.children.map((child) => (
        <FileTreeNode
          key={child.path}
          node={child}
          selectedFileId={selectedFileId}
          expandedDirs={expandedDirs}
          onSelect={onSelect}
          onToggleDir={onToggleDir}
          onDelete={onDelete}
          depth={depth + 1}
        />
      ))}
    </>
  )
}
